import React, { useState, useEffect, useRef } from 'react';
import './HindiPronunciationQuest.css';
import { HindiPhoneme, HINDI_PHONEMES } from '../data/hindiPhonemes';
import { phonemeToFilename } from '../data/phonemeAudioMapping';
import { getSpeechStreamingService, StreamingRecognitionResult, PronunciationScore } from '../services/speechStreamingService';
import { getBeginnerPhonemes } from '../data/gameConfig';
import guestSessionService from '../services/guestSessionService';
import LoginPrompt from './LoginPrompt';
import { useAuth } from '../contexts/AuthContext';

type GameState = 'level-select' | 'playing' | 'summary';

interface GameLevel {
  name: string;
  numSounds: number;
  audioGuide: boolean;
}

interface GameResult {
  phoneme: string;
  devanagari: string;
  score: number;
  attempts: number;
  userSaid?: string;
}

const gameLevels: Record<number, GameLevel> = {
  1: { name: 'Beginner', numSounds: 5, audioGuide: true },
  2: { name: 'Intermediate', numSounds: 25, audioGuide: true },
  3: { name: 'Advanced', numSounds: 40, audioGuide: false },
};

const HindiPronunciationQuest: React.FC = () => {
  const [gameState, setGameState] = useState<GameState>('level-select');
  const [selectedLevel, setSelectedLevel] = useState<number>(1);
  const [score, setScore] = useState(0);
  const [phonemes, setPhonemes] = useState<HindiPhoneme[]>([]);
  const [currentPhonemeIndex, setCurrentPhonemeIndex] = useState(0);
  const [attempts, setAttempts] = useState(1);
  const [gameResults, setGameResults] = useState<GameResult[]>([]);
  const [feedback, setFeedback] = useState<{ type: 'correct' | 'incorrect' | null; message: string }>({ type: null, message: '' });
  const [isListening, setIsListening] = useState(false);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [connectionError, setConnectionError] = useState<string>('');
  const [recognizedText, setRecognizedText] = useState<string>('');
  const [isPaused, setIsPaused] = useState(false);
  const [scoreAnimating, setScoreAnimating] = useState(false);
  const [scorePopup, setScorePopup] = useState<{ points: number; show: boolean }>({ points: 0, show: false });
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [loginPromptReason, setLoginPromptReason] = useState<string>('');

  const { user } = useAuth();
  
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const feedbackTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const isListeningRef = useRef<boolean>(false);
  const recognitionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasReceivedResultRef = useRef<boolean>(false);
  const lastRecognizedTextRef = useRef<string>('');
  const hasTrackedSessionRef = useRef<boolean>(false);

  // Helper function to stop audio capture
  const stopAudioCapture = () => {
    // Clean up audio resources
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }
    if (sourceRef.current) {
      sourceRef.current.disconnect();
      sourceRef.current = null;
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    // Clear recognition timeout
    if (recognitionTimeoutRef.current) {
      clearTimeout(recognitionTimeoutRef.current);
      recognitionTimeoutRef.current = null;
    }
  };

  // Get singleton speech service
  const speechService = getSpeechStreamingService();
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (speechService.isCurrentlyStreaming()) {
        speechService.stopStreaming();
      }
      stopAudioCapture();
    };
  }, []);

  // Initialize game when starting
  useEffect(() => {
    if (gameState === 'playing') {
      const level = gameLevels[selectedLevel];

      // Filter phonemes based on level
      let availablePhonemes = [...HINDI_PHONEMES];
      if (selectedLevel === 1) {
        // For beginner level, only use the configured beginner phonemes
        const beginnerSet = getBeginnerPhonemes();
        availablePhonemes = availablePhonemes.filter(p => beginnerSet.has(p.symbol));
      }

      // Select random phonemes from available set
      const shuffled = availablePhonemes.sort(() => Math.random() - 0.5);
      const selected = shuffled.slice(0, Math.min(level.numSounds, availablePhonemes.length));
      setPhonemes(selected);
      setCurrentPhonemeIndex(0);
      setScore(0);
      setAttempts(1);
      setGameResults([]);
      setFeedback({ type: null, message: '' });
      setRecognizedText(''); // Clear any previous recognized text
    }
  }, [gameState, selectedLevel]);

  // Play audio and start listening for current phoneme
  useEffect(() => {
    if (gameState === 'playing' && phonemes.length > 0 && currentPhonemeIndex < phonemes.length && !isPaused) {
      const currentPhoneme = phonemes[currentPhonemeIndex];
      const level = gameLevels[selectedLevel];
      
      // Clear recognized text when starting a new phoneme
      setRecognizedText('');
      lastRecognizedTextRef.current = '';
      
      // Clear any existing recognition timeout to prevent carryover
      if (recognitionTimeoutRef.current) {
        clearTimeout(recognitionTimeoutRef.current);
        recognitionTimeoutRef.current = null;
      }
      
      // Play audio if audio guide is enabled
      if (level.audioGuide) {
        playPhonemeAudio(currentPhoneme);
      }
      
      // Start listening after a short delay
      const listenDelay = level.audioGuide ? 1500 : 500;
      const listenTimeout = setTimeout(() => {
        startListening();
      }, listenDelay);
      
      return () => {
        clearTimeout(listenTimeout);
        // Clear recognition timeout on cleanup
        if (recognitionTimeoutRef.current) {
          clearTimeout(recognitionTimeoutRef.current);
          recognitionTimeoutRef.current = null;
        }
        if (isListening && speechService.isCurrentlyStreaming()) {
          speechService.stopStreaming();
          stopAudioCapture();
        }
      };
    }
    
    // Check for game end
    if (gameState === 'playing' && phonemes.length > 0 && currentPhonemeIndex >= phonemes.length) {
      setGameState('summary');
    }
  }, [gameState, currentPhonemeIndex, phonemes.length, selectedLevel, isPaused]);

  // Handle session tracking when game ends
  useEffect(() => {
    if (gameState === 'summary' && !user && !hasTrackedSessionRef.current) {
      console.log('[Session] Tracking game result - Score:', score, 'Games:', gameResults.length);

      const perfectScores = gameResults.filter(r => r.score === 10).length;
      guestSessionService.addGameResult(score, perfectScores);
      hasTrackedSessionRef.current = true; // Mark as tracked

      // Check if we should prompt for login
      const { shouldPrompt, reason } = guestSessionService.shouldPromptLogin();
      console.log('[Session] Should prompt login?', shouldPrompt, 'Reason:', reason);

      if (shouldPrompt) {
        setTimeout(() => {
          setLoginPromptReason(reason || '');
          setShowLoginPrompt(true);
        }, 1500); // Show after a brief delay so user sees their score first
      }
    }
  }, [gameState, user, gameResults, score]); // Run when game ends and track latest scores

  // Reset the result tracking ref when phoneme changes
  useEffect(() => {
    if (gameState === 'playing' && currentPhonemeIndex < phonemes.length) {
      // Reset the ref that tracks if we received a result
      hasReceivedResultRef.current = false;
      // Attempts will be reset when transitioning to new phoneme
    }
  }, [currentPhonemeIndex]);

  const playPhonemeAudio = (phoneme: HindiPhoneme) => {
    // Get the correct filename from the mapping
    const filename = phonemeToFilename[phoneme.symbol];
    
    if (!filename) {
      console.warn(`No audio file mapping found for phoneme: ${phoneme.symbol}`);
      setAudioPlaying(false);
      return;
    }
    
    const audioPath = `/audio/hindi-phonemes/${filename}`;
    console.log(`Playing audio for ${phoneme.symbol}: ${audioPath}`);
    
    if (audioRef.current) {
      audioRef.current.pause();
    }
    
    setAudioPlaying(true);
    audioRef.current = new Audio(audioPath);
    audioRef.current.onended = () => {
      setAudioPlaying(false);
      console.log('Audio playback completed');
    };
    audioRef.current.onerror = (e) => {
      setAudioPlaying(false);
      console.error(`Audio playback failed for ${audioPath}:`, e);
    };
    
    audioRef.current.play().catch(err => {
      console.error(`Failed to play audio for ${phoneme.symbol}:`, err);
      setAudioPlaying(false);
    });
  };

  const startListening = async () => {
    if (isListening || isPaused) return;

    console.log('[Game] Starting listening...');
    
    // Check if socket is connected first
    if (!speechService.isConnected()) {
      console.log('[Game] Socket not connected, attempting to reconnect...');
      setConnectionError('Connecting to speech service...');
      
      // The isConnected() method will try to reconnect if disconnected
      // Give it more time to connect
      let retries = 0;
      const maxRetries = 20; // Increase retries
      while (retries < maxRetries) {
        if (speechService.isConnected()) {
          console.log('[Game] Successfully connected after', retries, 'retries');
          break;
        }
        await new Promise(resolve => setTimeout(resolve, 500));
        retries++;
      }
      
      if (!speechService.isConnected()) {
        console.error('[Game] Failed to connect to speech service after', maxRetries, 'retries');
        setConnectionError('Unable to connect to speech service. Please check your internet connection and refresh the page.');
        setIsListening(false);
        isListeningRef.current = false;
        return;
      }
      
      setConnectionError('');
    }
    
    setIsListening(true);
    isListeningRef.current = true;
    hasReceivedResultRef.current = false;
    // Don't clear recognized text here - let it persist to show user what was recognized
    
    const success = await speechService.startStreaming(
      handleRecognitionResult,
      handlePronunciationScores,
      handleError,
      48000,
      'hi-IN'  // Use Hindi language recognition
    );
    
    console.log('[Game] Streaming started:', success);
    
    if (success) {
      // Set a timeout for recognition - if no result in 5 seconds, count as incorrect
      if (recognitionTimeoutRef.current) {
        clearTimeout(recognitionTimeoutRef.current);
      }
      
      recognitionTimeoutRef.current = setTimeout(() => {
        if (isListeningRef.current && !hasReceivedResultRef.current && gameState === 'playing') {
          console.log('[Game] No recognition result after 7 seconds, treating as incorrect');
          // Clear recognized text when no result
          setRecognizedText('');
          // Call handleIncorrectAnswer which will handle feedback and attempts
          handleIncorrectAnswer();
        }
      }, 7000);
      
      // Start capturing audio from microphone
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ 
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          } 
        });
        
        mediaStreamRef.current = stream;
        
        // Create audio context with specific sample rate
        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 48000 });
        audioContextRef.current = audioContext;
        
        const source = audioContext.createMediaStreamSource(stream);
        sourceRef.current = source;
        
        // Use ScriptProcessorNode for now (will need to migrate to AudioWorklet later)
        const bufferSize = 2048;
        const processor = audioContext.createScriptProcessor(bufferSize, 1, 1);
        processorRef.current = processor;
        
        let chunkCount = 0;
        processor.onaudioprocess = (e) => {
          if (!isListeningRef.current) return;
          
          const audioData = e.inputBuffer.getChannelData(0);
          
          // Log every 50th chunk to avoid spam
          if (chunkCount % 50 === 0) {
            console.log(`[Game] Sending audio chunk ${chunkCount}, buffer size: ${audioData.length}`);
          }
          chunkCount++;
          
          speechService.sendAudioChunk(audioData, audioContext.sampleRate);
        };
        
        source.connect(processor);
        processor.connect(audioContext.destination);
        
        console.log('[Game] Audio capture started successfully');
        console.log('[Game] Sample rate:', audioContext.sampleRate);
        console.log('[Game] Buffer size:', bufferSize);
      } catch (error) {
        console.error('[Game] Failed to start microphone:', error);
        setIsListening(false);
        isListeningRef.current = false;
        speechService.stopStreaming();
      }
    } else {
      console.error('[Game] Failed to start streaming - socket may have disconnected');
      setIsListening(false);
      isListeningRef.current = false;
      setConnectionError('Lost connection to speech service. Please try again.');
      
      // Try to reconnect for next attempt
      setTimeout(() => {
        speechService.isConnected(); // Trigger reconnection
      }, 1000);
    }
  };

  const handleRecognitionResult = (result: StreamingRecognitionResult) => {
    console.log('[Game] Recognition result received:', result);
    
    if (!result.isFinal || gameState !== 'playing') {
      console.log('[Game] Ignoring result - isFinal:', result.isFinal, 'gameState:', gameState);
      return;
    }
    
    // Mark that we received a result
    hasReceivedResultRef.current = true;
    
    // Clear the timeout since we got a result
    if (recognitionTimeoutRef.current) {
      clearTimeout(recognitionTimeoutRef.current);
    }
    
    const currentPhoneme = phonemes[currentPhonemeIndex];
    const recognized = result.transcript.trim().toLowerCase();
    
    // Store the recognized text for display and in ref for later use
    const recognizedTranscript = result.transcript.trim();
    setRecognizedText(recognizedTranscript);
    lastRecognizedTextRef.current = recognizedTranscript;
    
    console.log('[Game] Recognized (Hindi):', recognized);
    console.log('[Game] Expected phoneme:', currentPhoneme.symbol);
    console.log('[Game] Expected Devanagari:', currentPhoneme.devanagari);
    
    // For Hindi recognition, we expect Devanagari or romanized results
    // Check against Devanagari character and example words
    const expectedVariants = [
      currentPhoneme.devanagari.toLowerCase(),
      // Extract just the Hindi letters from examples (remove transliteration)
      ...currentPhoneme.examples.map(ex => {
        // Examples are in format "हिंदी (hindi)" - extract the Devanagari part
        const hindiPart = ex.split('(')[0].trim().toLowerCase();
        return hindiPart;
      })
    ];
    
    // Also check for individual phoneme sounds in romanized form
    // Map common Hindi recognition patterns
    const romanizedVariants = getHindiRomanizedVariants(currentPhoneme);
    
    console.log('[Game] Expected Devanagari variants:', expectedVariants);
    console.log('[Game] Expected romanized variants:', romanizedVariants);
    
    // Check if recognized text matches any expected variant
    // Be more permissive - if the user says a word containing the phoneme, it's correct
    const isCorrect = expectedVariants.some(variant => {
      // Direct match
      if (recognized === variant) return true;

      // Check if the word contains the phoneme anywhere
      // This handles cases where the phoneme appears with matras or in compound letters
      if (recognized.includes(variant)) return true;

      // For single character phonemes, also check at word start
      if (variant.length === 1 && recognized.startsWith(variant)) return true;

      return false;
    }) || romanizedVariants.some(variant =>
      recognized === variant ||
      recognized.startsWith(variant) ||
      recognized.includes(variant) // Also check if romanized form appears anywhere
    );
    
    console.log('[Game] Is correct:', isCorrect);
    
    if (isCorrect) {
      handleCorrectAnswer();
    } else {
      handleIncorrectAnswer();
    }
  };

  // Helper function to get romanized variants for Hindi phonemes
  const getHindiRomanizedVariants = (phoneme: HindiPhoneme): string[] => {
    // Map phoneme symbols to common romanized forms that Hindi recognition might return
    const romanizationMap: { [key: string]: string[] } = {
      // Vowels
      'ə': ['a', 'uh'],
      'aː': ['aa', 'a'],
      'ɪ': ['i', 'e'],
      'iː': ['ee', 'i'],
      'ʊ': ['u', 'oo'],
      'uː': ['oo', 'u'],
      'eː': ['e', 'ay'],
      'ɛː': ['ai', 'ay'],
      'oː': ['o', 'oh'],
      'ɔː': ['au', 'ow'],
      
      // Consonants
      'k': ['ka', 'k'],
      'kʰ': ['kha', 'kh'],
      'g': ['ga', 'g'],
      'gʱ': ['gha', 'gh'],
      'tʃ': ['cha', 'ch'],
      'tʃʰ': ['chha', 'chh'],
      'dʒ': ['ja', 'j'],
      'dʒʱ': ['jha', 'jh'],
      'ʈ': ['ta', 't'],
      'ʈʰ': ['tha', 'th'],
      'ɖ': ['da', 'd'],
      'ɖʱ': ['dha', 'dh'],
      't̪': ['ta', 't'],
      't̪ʰ': ['tha', 'th'],
      'd̪': ['da', 'd'],
      'd̪ʱ': ['dha', 'dh'],
      'p': ['pa', 'p'],
      'pʰ': ['pha', 'ph', 'fa', 'f'],
      'b': ['ba', 'b'],
      'bʱ': ['bha', 'bh'],
      
      // Nasals
      'ŋ': ['nga', 'ng'],
      'ɲ': ['nya', 'ny', 'gya'],
      'ɳ': ['na', 'n'],
      'n': ['na', 'n'],
      'm': ['ma', 'm'],
      
      // Others
      'j': ['ya', 'y'],
      'r': ['ra', 'r'],
      'l': ['la', 'l'],
      'ʋ': ['va', 'wa', 'v', 'w'],
      'ʃ': ['sha', 'sh'],
      'ʂ': ['sha', 'sh'],
      's': ['sa', 's'],
      'h': ['ha', 'h'],
      'ɽ': ['ra', 'da', 'r', 'd'],
      'ɽʱ': ['rha', 'dha']
    };
    
    return romanizationMap[phoneme.symbol] || [];
  };

  const handlePronunciationScores = (scores: PronunciationScore[]) => {
    // Can use pronunciation scores for more detailed feedback
    console.log('[Game] Pronunciation scores:', scores);
  };

  const handleError = (error: any) => {
    console.error('[Game] Speech recognition error:', error);
    setIsListening(false);
    isListeningRef.current = false;
  };

  const handleCorrectAnswer = () => {
    // Scoring: 10 for first try, 8 for second, 6 for third, 4 for fourth, 2 for fifth
    const points = Math.max(2, 10 - (attempts - 1) * 2);
    const currentPhoneme = phonemes[currentPhonemeIndex];

    // Stop listening
    speechService.stopStreaming();
    stopAudioCapture();
    setIsListening(false);
    isListeningRef.current = false;

    // Trigger score animation
    setScoreAnimating(true);
    setScorePopup({ points, show: true });

    // Update score and results
    setScore(prev => prev + points);
    setGameResults(prev => [...prev, {
      phoneme: currentPhoneme.symbol,
      devanagari: currentPhoneme.devanagari,
      score: points,
      attempts: attempts,
      userSaid: lastRecognizedTextRef.current || recognizedText
    }]);

    // Show feedback with special message for perfect score
    const feedbackMessage = points === 10
      ? `Perfect! First try! +${points} points 🌟`
      : `Excellent! +${points} points`;
    setFeedback({ type: 'correct', message: feedbackMessage });

    // Reset animations after delay
    setTimeout(() => {
      setScoreAnimating(false);
    }, 600);

    setTimeout(() => {
      setScorePopup({ points: 0, show: false });
    }, 1500);
    
    // Clear previous timeout if exists
    if (feedbackTimeoutRef.current) {
      clearTimeout(feedbackTimeoutRef.current);
    }
    
    // Move to next phoneme after delay
    feedbackTimeoutRef.current = setTimeout(() => {
      setFeedback({ type: null, message: '' });
      setRecognizedText(''); // Clear recognized text for next phoneme
      lastRecognizedTextRef.current = ''; // Clear the ref too
      setAttempts(1); // Reset attempts for next phoneme
      setCurrentPhonemeIndex(prev => prev + 1);
    }, 2000);
  };

  const handleIncorrectAnswer = () => {
    // Stop current streaming
    speechService.stopStreaming();
    stopAudioCapture();
    setIsListening(false);
    isListeningRef.current = false;

    // Use functional update to ensure we get the latest attempts value
    setAttempts(prevAttempts => {
      const newAttempts = prevAttempts + 1;

      // Check if we've reached max attempts (5)
      if (newAttempts >= 5) {
        // Max attempts reached, give 0 points and move on
        const currentPhoneme = phonemes[currentPhonemeIndex];

        setFeedback({ type: 'incorrect', message: 'Max attempts reached! Moving on...' });

        // Record with 0 points
        setGameResults(prev => [...prev, {
          phoneme: currentPhoneme.symbol,
          devanagari: currentPhoneme.devanagari,
          score: 0,
          attempts: newAttempts,
          userSaid: lastRecognizedTextRef.current || 'No response'
        }]);

        // Clear previous timeout if exists
        if (feedbackTimeoutRef.current) {
          clearTimeout(feedbackTimeoutRef.current);
        }

        // Move to next phoneme after delay
        feedbackTimeoutRef.current = setTimeout(() => {
          setFeedback({ type: null, message: '' });
          setRecognizedText(''); // Clear recognized text for next phoneme
          lastRecognizedTextRef.current = ''; // Clear the ref too
          setAttempts(1); // Reset attempts for next phoneme
          setCurrentPhonemeIndex(prev => prev + 1);
        }, 2000);

        // Don't reset here, let the setTimeout handle it
        return newAttempts;
      } else {
        // Still have attempts left
        setFeedback({ type: 'incorrect', message: `Try again! (${5 - newAttempts} attempts left)` });

        // Clear previous timeout if exists
        if (feedbackTimeoutRef.current) {
          clearTimeout(feedbackTimeoutRef.current);
        }

        // Continue listening after showing feedback
        feedbackTimeoutRef.current = setTimeout(() => {
          setFeedback({ type: null, message: '' });
          startListening();
        }, 1500);

        // Return the incremented attempts
        return newAttempts;
      }
    });
  };

  const handleLevelSelect = (level: number) => {
    setSelectedLevel(level);
    setGameState('playing');
  };

  const handlePlayAgain = () => {
    setGameState('level-select');
    setScore(0);
    setPhonemes([]);
    setCurrentPhonemeIndex(0);
    setAttempts(1);
    setGameResults([]);
    setIsPaused(false);
    hasTrackedSessionRef.current = false; // Reset session tracking for new game
  };

  const handlePauseToggle = () => {
    if (isPaused) {
      // Resume the game
      setIsPaused(false);
      // Restart listening after unpausing
      if (!isListening && gameState === 'playing' && currentPhonemeIndex < phonemes.length) {
        setTimeout(() => {
          startListening();
        }, 500);
      }
    } else {
      // Pause the game
      setIsPaused(true);
      // Stop listening when paused
      if (isListening || speechService.isCurrentlyStreaming()) {
        speechService.stopStreaming();
        stopAudioCapture();
        setIsListening(false);
        isListeningRef.current = false;
      }
      // Clear any running timeouts
      if (recognitionTimeoutRef.current) {
        clearTimeout(recognitionTimeoutRef.current);
        recognitionTimeoutRef.current = null;
      }
      if (feedbackTimeoutRef.current) {
        clearTimeout(feedbackTimeoutRef.current);
        feedbackTimeoutRef.current = null;
      }
    }
  };

  // Render level selection
  if (gameState === 'level-select') {
    return (
      <div className="quest-container">
        <div className="quest-header">
          <h1>🎮 Game Time!</h1>
          <p>Master Hindi sounds through fun challenges!</p>
        </div>
        
        <div className="level-select">
          <h2>Choose Your Level</h2>
          <div className="level-cards">
            {Object.entries(gameLevels).map(([level, config]) => (
              <div 
                key={level} 
                className="level-card"
                onClick={() => handleLevelSelect(Number(level))}
              >
                <div className="level-icon">
                  {level === '1' ? '🌱' : level === '2' ? '🌿' : '🌳'}
                </div>
                <h3>{config.name}</h3>
                <p>{config.numSounds} sounds</p>
                <p>{config.audioGuide ? '🔊 Audio guide' : '🔇 No audio'}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Render game playing state
  if (gameState === 'playing') {
    const currentPhoneme = phonemes[currentPhonemeIndex];
    const progress = ((currentPhonemeIndex + 1) / phonemes.length) * 100;
    
    return (
      <div className="quest-container">
        <div className="game-header">
          <div className="score-display">
            <span className="score-label">Score:</span>
            <span className={`score-value ${scoreAnimating ? 'score-animating' : ''}`}>
              {score}
              {scorePopup.show && (
                <span className="score-popup">+{scorePopup.points}</span>
              )}
            </span>
          </div>
          <div className="progress-info">
            Sound {currentPhonemeIndex + 1} of {phonemes.length}
          </div>
          <button
            className="pause-btn"
            onClick={handlePauseToggle}
            style={{
              marginLeft: '20px',
              padding: '8px 16px',
              fontSize: '16px',
              backgroundColor: isPaused ? '#4CAF50' : '#FF9800',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {isPaused ? '▶️ Resume' : '⏸️ Pause'}
          </button>
        </div>
        
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${progress}%` }} />
        </div>
        
        <div className="game-content">
          {currentPhoneme && (
            <div className={`phoneme-card ${feedback.type ? `feedback-${feedback.type}` : ''}`}>
              <div className="phoneme-display">
                <div className="phoneme-devanagari">{currentPhoneme.devanagari}</div>
                <div className="phoneme-description">{currentPhoneme.description}</div>
              </div>
              
              {feedback.type && (
                <div className={`feedback-message ${feedback.message.includes('Perfect!') ? 'perfect-score' : ''}`}>
                  {feedback.message}
                </div>
              )}
              
              <div className="audio-controls">
                <button 
                  className="replay-audio-btn"
                  onClick={() => playPhonemeAudio(currentPhoneme)}
                  disabled={audioPlaying}
                  title="Click to hear the sound again"
                >
                  {audioPlaying ? '⏸️ Playing...' : '🔊 Play Sound'}
                </button>
              </div>
              
              <div className="listening-indicator">
                {connectionError && (
                  <div className="connection-error">
                    <p>⚠️ {connectionError}</p>
                  </div>
                )}
                {isPaused && (
                  <div style={{ padding: '20px', fontSize: '18px', color: '#FF9800' }}>
                    <p>⏸️ Game Paused</p>
                    <p style={{ fontSize: '14px', marginTop: '10px' }}>Click Resume to continue</p>
                  </div>
                )}
                {!isPaused && isListening && !feedback.type && !connectionError && (
                  <>
                    <div className="mic-icon pulse">🎤</div>
                    <p>Listening... Say the sound!</p>
                  </>
                )}
                {recognizedText && !isPaused && (
                  <div className="recognized-text">
                    <p className="recognized-label">You said:</p>
                    <p className="recognized-value">{recognizedText}</p>
                  </div>
                )}
              </div>
              
              {(isListening || feedback.type) && (
                <div className="attempt-indicator">
                  Attempt: {attempts} / 5
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Render summary state
  if (gameState === 'summary') {
    const masteredSounds = gameResults.filter(r => r.score >= 8);
    const practiceZone = gameResults.filter(r => r.score > 0 && r.score < 8);
    const failedSounds = gameResults.filter(r => r.score === 0);
    const averageScore = gameResults.length > 0
      ? Math.round(gameResults.reduce((sum, r) => sum + r.score, 0) / gameResults.length)
      : 0;

    // Check if perfect score (10 points per phoneme)
    const maxPossibleScore = gameResults.length * 10;
    const isPerfectScore = score === maxPossibleScore;

    return (<>
      <div className="quest-container">
        <div className="summary-header">
          {isPerfectScore ? (
            <h1>🎉 You nailed it! Perfect score!</h1>
          ) : (
            <h1>🏆 Game Complete!</h1>
          )}
          <div className="final-score">
            <div className="score-label">Final Score</div>
            <div className="score-value">{score}</div>
          </div>
          <div className="average-score">
            Average per sound: {averageScore} points
          </div>
        </div>
        
        <div className="summary-content">
          {masteredSounds.length > 0 && (
            <div className="summary-section mastered">
              <h3>✨ Mastered Sounds ({masteredSounds.length})</h3>
              <div className="results-table" style={{ display: 'table', width: '100%' }}>
                <div className="table-header" style={{ display: 'table-row', fontWeight: 'bold', borderBottom: '2px solid #ddd', paddingBottom: '8px' }}>
                  <span style={{ display: 'table-cell', padding: '8px', width: '80px' }}>Sound</span>
                  <span style={{ display: 'table-cell', padding: '8px' }}>You Said</span>
                  <span style={{ display: 'table-cell', padding: '8px', width: '80px', textAlign: 'right' }}>Score</span>
                </div>
                {masteredSounds.map((result, idx) => (
                  <div key={idx} className="sound-item" style={{ display: 'table-row' }}>
                    <span className="sound-devanagari" style={{ display: 'table-cell', padding: '8px' }}>{result.devanagari}</span>
                    <span className="user-said" style={{ display: 'table-cell', padding: '8px', color: '#666', fontStyle: 'italic' }}>
                      "{result.userSaid || 'Unknown'}"
                    </span>
                    <span className="sound-score" style={{ display: 'table-cell', padding: '8px', textAlign: 'right' }}>{result.score}pts</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {practiceZone.length > 0 && (
            <div className="summary-section practice">
              <h3>📚 Practice Zone ({practiceZone.length})</h3>
              <div className="results-table" style={{ display: 'table', width: '100%' }}>
                <div className="table-header" style={{ display: 'table-row', fontWeight: 'bold', borderBottom: '2px solid #ddd', paddingBottom: '8px' }}>
                  <span style={{ display: 'table-cell', padding: '8px', width: '80px' }}>Sound</span>
                  <span style={{ display: 'table-cell', padding: '8px' }}>You Said</span>
                  <span style={{ display: 'table-cell', padding: '8px', width: '80px', textAlign: 'right' }}>Score</span>
                  <span style={{ display: 'table-cell', padding: '8px', width: '100px', textAlign: 'right' }}>Attempts</span>
                </div>
                {practiceZone.map((result, idx) => (
                  <div key={idx} className="sound-item" style={{ display: 'table-row' }}>
                    <span className="sound-devanagari" style={{ display: 'table-cell', padding: '8px' }}>{result.devanagari}</span>
                    <span className="user-said" style={{ display: 'table-cell', padding: '8px', color: '#666', fontStyle: 'italic' }}>
                      "{result.userSaid || 'Unknown'}"
                    </span>
                    <span className="sound-score" style={{ display: 'table-cell', padding: '8px', textAlign: 'right' }}>{result.score}pts</span>
                    <span className="attempts-badge" style={{ display: 'table-cell', padding: '8px', textAlign: 'right' }}>
                      {result.attempts} {result.attempts === 1 ? 'try' : 'tries'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {failedSounds.length > 0 && (
            <div className="summary-section failed">
              <h3>🔄 Need More Practice ({failedSounds.length})</h3>
              <div className="results-table" style={{ display: 'table', width: '100%' }}>
                <div className="table-header" style={{ display: 'table-row', fontWeight: 'bold', borderBottom: '2px solid #ddd', paddingBottom: '8px' }}>
                  <span style={{ display: 'table-cell', padding: '8px', width: '80px' }}>Sound</span>
                  <span style={{ display: 'table-cell', padding: '8px' }}>You Said</span>
                  <span style={{ display: 'table-cell', padding: '8px', width: '80px', textAlign: 'right' }}>Score</span>
                  <span style={{ display: 'table-cell', padding: '8px', width: '100px', textAlign: 'right' }}>Attempts</span>
                </div>
                {failedSounds.map((result, idx) => (
                  <div key={idx} className="sound-item" style={{ display: 'table-row' }}>
                    <span className="sound-devanagari" style={{ display: 'table-cell', padding: '8px' }}>{result.devanagari}</span>
                    <span className="user-said" style={{ display: 'table-cell', padding: '8px', color: '#666', fontStyle: 'italic' }}>
                      "{result.userSaid || 'No response'}"
                    </span>
                    <span className="sound-score" style={{ display: 'table-cell', padding: '8px', textAlign: 'right' }}>0pts</span>
                    <span className="attempts-badge failed" style={{ display: 'table-cell', padding: '8px', textAlign: 'right' }}>
                      {result.attempts} attempts
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <div className="summary-actions">
          <button className="play-again-btn" onClick={handlePlayAgain}>
            🎮 Play Again?
          </button>
        </div>
      </div>

      {/* Login Prompt Modal */}
      <LoginPrompt
        show={showLoginPrompt}
        onClose={() => setShowLoginPrompt(false)}
        reason={loginPromptReason}
        guestScore={guestSessionService.getTotalScore()}
        gamesPlayed={guestSessionService.getGamesPlayed()}
      />
    </>);
  }

  return null;
};

export default HindiPronunciationQuest;